import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Lock, ArrowRight, ChevronLeft, ChevronRight, Mail, Check, Users, Lightbulb, Flame, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Section Divider Component
const SectionDivider = () => (
  <div 
    className="w-full h-px mx-auto my-0"
    style={{
      background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.12), transparent)',
      margin: '0 60px',
      maxWidth: 'calc(100% - 120px)'
    }}
  />
);

// Nav Component
const Nav = ({ onSignIn, onApply }) => {
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToApply = () => {
    const applySection = document.getElementById('apply-section');
    if (applySection) {
      applySection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isSticky ? 'bg-[rgba(10,9,8,0.96)] backdrop-blur-[12px] border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center">
          <span style={{ fontFamily: 'Georgia, Cormorant Garamond, serif' }}>
            <span className="text-[#d4af37]">Prayer</span>
            <span className="text-white/70"> to </span>
            <span className="text-[#d4af37]">Prototype</span>
          </span>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={scrollToApply}
            className="px-4 py-2 rounded-full text-[0.85rem] font-semibold transition-all hover:scale-105"
            style={{
              background: '#d4af37',
              color: '#0a0908'
            }}
            data-testid="nav-apply-btn"
          >
            Apply
          </button>
        </div>
      </div>
    </nav>
  );
};

// Hero Section - Updated with two CTA buttons
const HeroSection = () => {
  const scrollToApply = () => {
    const applySection = document.getElementById('apply-section');
    if (applySection) {
      applySection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToSprint = () => {
    const sprintSection = document.getElementById('sprint-section');
    if (sprintSection) {
      sprintSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-20" style={{ position: 'relative', overflow: 'hidden' }} data-testid="hero-section">
      {/* Background Image Layer */}
      <div 
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_bc3a537f-1f87-4400-acd2-c67793997da6/artifacts/8ge6z85q_ChatGPT%20Image%20Mar%205%2C%202026%2C%2002_43_46%20PM.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 30%',
          opacity: 0.28,
          zIndex: 0,
          pointerEvents: 'none'
        }}
      />
      {/* Gradient Overlay */}
      <div 
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to bottom, rgba(8,7,6,0.25) 0%, rgba(8,7,6,0.45) 50%, rgba(8,7,6,1) 100%)',
          zIndex: 1,
          pointerEvents: 'none'
        }}
      />
    
      <div className="max-w-4xl mx-auto text-center" style={{ position: 'relative', zIndex: 2 }}>
        {/* Eyebrow Pill */}
        <div 
          className="inline-block mb-7"
          style={{
            position: 'relative',
            zIndex: 2,
            background: 'rgba(212,175,55,0.13)',
            color: '#d4af37',
            border: '1px solid rgba(212,175,55,0.22)',
            borderRadius: '20px',
            padding: '5px 16px',
            fontSize: '0.68rem',
            letterSpacing: '1.5px',
            textTransform: 'uppercase'
          }}
        >
          Created by a Harvard MBA and Techstars-backed founder
        </div>
        
        {/* Headline */}
        <h1 
          style={{
            position: 'relative',
            zIndex: 2,
            fontSize: 'clamp(2.8rem, 6vw, 4.4rem)',
            fontWeight: 900,
            letterSpacing: '-1.5px',
            lineHeight: 1.06
          }}
        >
          <span className="text-[#d4af37] block">Stop Praying About It.</span>
          <span className="text-white block">Start Building It.</span>
        </h1>
        
        {/* Scripture */}
        <p 
          className="mt-[22px]"
          style={{
            position: 'relative',
            zIndex: 2,
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            color: 'rgba(212,175,55,0.8)',
            fontSize: '1.05rem'
          }}
        >
          &ldquo;Faith without works is dead.&rdquo; — James 2:17
        </p>
        
        {/* Subheadline */}
        <p 
          className="mx-auto"
          style={{
            position: 'relative',
            zIndex: 2,
            color: 'rgba(255,255,255,0.55)',
            fontSize: '1rem',
            maxWidth: '520px',
            margin: '18px auto 40px'
          }}
        >
          A 7-day AI-powered sprint that takes you from God-given idea to live prototype with faith at the center.
        </p>
        
        {/* Two CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4" style={{ position: 'relative', zIndex: 2 }}>
          {/* Primary Button - Apply */}
          <button
            onClick={scrollToApply}
            className="px-8 py-4 rounded-full font-bold text-base transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
            style={{
              background: '#d4af37',
              color: '#0a0908'
            }}
            data-testid="hero-apply-btn"
          >
            Apply for the Sprint <ArrowRight className="w-4 h-4 ml-2 inline" />
          </button>
          
          {/* Secondary Button - How it works */}
          <button
            onClick={scrollToSprint}
            className="px-8 py-4 rounded-full font-semibold text-base transition-all duration-300 hover:bg-white/5"
            style={{
              background: 'transparent',
              color: '#ffffff',
              border: '1px solid rgba(255,255,255,0.25)'
            }}
            data-testid="hero-how-it-works-btn"
          >
            How it works
          </button>
        </div>
        
        {/* Muted text below buttons */}
        <p 
          className="mt-5"
          style={{
            position: 'relative',
            zIndex: 2,
            color: 'rgba(255,255,255,0.35)',
            fontSize: '0.82rem'
          }}
        >
          20 spots available · Summer 2026
        </p>
      </div>
    </section>
  );
};

// "This Is For You If" Section - NEW
const ThisIsForYouSection = () => {
  const cards = [
    {
      icon: <Users className="w-6 h-6" />,
      text: "You have an audience but nothing to sell them"
    },
    {
      icon: <Lightbulb className="w-6 h-6" />,
      text: "Your idea has been living in your notes app for months"
    },
    {
      icon: <Flame className="w-6 h-6" />,
      text: "God didn't give you a platform to stay silent"
    }
  ];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0a0908]" data-testid="this-is-for-you-section">
      <div className="max-w-5xl mx-auto">
        {/* Section Label */}
        <div className="text-center mb-12">
          <span 
            className="inline-block"
            style={{
              background: 'rgba(212,175,55,0.13)',
              color: '#d4af37',
              border: '1px solid rgba(212,175,55,0.22)',
              borderRadius: '20px',
              padding: '5px 16px',
              fontSize: '0.68rem',
              letterSpacing: '1.5px',
              textTransform: 'uppercase'
            }}
          >
            THIS IS FOR YOU IF
          </span>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cards.map((card, index) => (
            <div 
              key={index}
              className="p-6 rounded-2xl transition-all duration-300 hover:-translate-y-1"
              style={{
                background: '#111009',
                border: '1px solid rgba(212,175,55,0.18)'
              }}
            >
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                style={{
                  background: 'rgba(212,175,55,0.1)',
                  color: '#d4af37'
                }}
              >
                {card.icon}
              </div>
              <p className="text-white text-lg font-medium leading-relaxed">
                {card.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// "What You Build" Section - Wraps the interactive product demo
const WhatYouBuildSection = () => {
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0a0908]" data-testid="what-you-build-section">
      <div className="max-w-5xl mx-auto">
        {/* Section Heading */}
        <div className="text-center mb-12">
          <span 
            className="inline-block mb-4"
            style={{
              background: 'rgba(212,175,55,0.13)',
              color: '#d4af37',
              border: '1px solid rgba(212,175,55,0.22)',
              borderRadius: '20px',
              padding: '5px 16px',
              fontSize: '0.68rem',
              letterSpacing: '1.5px',
              textTransform: 'uppercase'
            }}
          >
            WHAT YOU BUILD
          </span>
          <h2 className="text-white text-2xl sm:text-3xl font-bold">
            Here&apos;s exactly what you&apos;ll walk out with.
          </h2>
        </div>

        {/* Interactive Product Demo */}
        <InsideTheAppSection />
      </div>
    </section>
  );
};

// Ticker Section - Ideas that became something
const TickerSection = () => {
  return (
    <section className="ptp-ticker-section" data-testid="ticker-section">
      <style>{`
        .ptp-ticker-section {
          padding: 80px 0 72px;
        }
        .ptp-ticker-eyebrow {
          text-align: center;
          margin-bottom: 52px;
          padding: 0 24px;
        }
        .ptp-ticker-pill {
          display: inline-block;
          background: rgba(212,175,55,0.07);
          color: rgba(255,255,255,0.35);
          border: 1px solid rgba(212,175,55,0.12);
          border-radius: 20px;
          padding: 4px 14px;
          font-size: 0.58rem;
          letter-spacing: 2px;
          text-transform: uppercase;
          font-weight: 600;
          margin-bottom: 16px;
        }
        .ptp-ticker-eyebrow h2 {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(1.8rem, 3vw, 2.6rem);
          font-weight: 700;
          color: #ffffff;
          line-height: 1.15;
          margin-bottom: 10px;
        }
        .ptp-ticker-eyebrow h2 em {
          color: #d4af37;
          font-style: italic;
        }
        .ptp-ticker-eyebrow p {
          font-size: 0.85rem;
          color: rgba(255,255,255,0.35);
          line-height: 1.6;
        }
        .ptp-ticker-wrap {
          overflow: hidden;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .ptp-ticker-row {
          display: flex;
          gap: 14px;
          animation: ptpTickerScroll 28s linear infinite;
          width: max-content;
        }
        .ptp-ticker-row.ptp-reverse {
          animation: ptpTickerReverse 32s linear infinite;
        }
        @keyframes ptpTickerScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes ptpTickerReverse {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
        .ptp-ticker-item {
          display: flex;
          align-items: center;
          gap: 10px;
          background: #111009;
          border: 1px solid rgba(255,255,255,0.05);
          border-radius: 50px;
          padding: 10px 18px 10px 12px;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .ptp-ticker-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #d4af37;
          opacity: 0.5;
          flex-shrink: 0;
        }
        .ptp-ticker-product {
          font-size: 0.8rem;
          font-weight: 600;
          color: rgba(255,255,255,0.7);
        }
        .ptp-ticker-builder {
          font-size: 0.72rem;
          color: rgba(255,255,255,0.25);
          padding-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.08);
        }
        .ptp-ticker-type {
          font-size: 0.55rem;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: rgba(212,175,55,0.45);
          font-weight: 700;
          background: rgba(212,175,55,0.06);
          padding: 2px 7px;
          border-radius: 20px;
        }
        .ptp-ticker-stats {
          display: flex;
          justify-content: center;
          gap: 60px;
          margin-top: 52px;
          padding: 0 40px;
          flex-wrap: wrap;
        }
        .ptp-stat {
          text-align: center;
        }
        .ptp-stat-num {
          font-family: 'Cormorant Garamond', serif;
          font-size: 2.8rem;
          font-weight: 700;
          color: #d4af37;
          line-height: 1;
          display: block;
          margin-bottom: 4px;
        }
        .ptp-stat-label {
          font-size: 0.65rem;
          color: rgba(255,255,255,0.3);
          letter-spacing: 0.5px;
          line-height: 1.4;
        }
      `}</style>

      <div className="ptp-ticker-eyebrow">
        <div className="ptp-ticker-pill">Build yours in less than 7 days</div>
        <h2>Ideas that <em>became</em> something.</h2>
        <p>Every idea starts somewhere. Yours can start today.</p>
      </div>

      <div className="ptp-ticker-wrap">

        <div className="ptp-ticker-row">
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Covenant Match</div><div className="ptp-ticker-builder">DeAndre · Memphis</div><div className="ptp-ticker-type">App</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Corporate to Calling</div><div className="ptp-ticker-builder">Jasmine · Atlanta</div><div className="ptp-ticker-type">Course</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">FaithOS</div><div className="ptp-ticker-builder">Marcus · Charlotte</div><div className="ptp-ticker-type">SaaS</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Grace and Grow Daycare</div><div className="ptp-ticker-builder">Priya · Houston</div><div className="ptp-ticker-type">Community</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">The Kingdom Builder Summit</div><div className="ptp-ticker-builder">Tanya · Dallas</div><div className="ptp-ticker-type">Popup Event</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Renewed Minds Podcast Academy</div><div className="ptp-ticker-builder">Andre · Chicago</div><div className="ptp-ticker-type">Digital Product</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Scroll Stopper Studio</div><div className="ptp-ticker-builder">Keisha · Los Angeles</div><div className="ptp-ticker-type">Brick and Mortar</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Covenant Match</div><div className="ptp-ticker-builder">DeAndre · Memphis</div><div className="ptp-ticker-type">App</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Corporate to Calling</div><div className="ptp-ticker-builder">Jasmine · Atlanta</div><div className="ptp-ticker-type">Course</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">FaithOS</div><div className="ptp-ticker-builder">Marcus · Charlotte</div><div className="ptp-ticker-type">SaaS</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Grace and Grow Daycare</div><div className="ptp-ticker-builder">Priya · Houston</div><div className="ptp-ticker-type">Community</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">The Kingdom Builder Summit</div><div className="ptp-ticker-builder">Tanya · Dallas</div><div className="ptp-ticker-type">Popup Event</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Renewed Minds Podcast Academy</div><div className="ptp-ticker-builder">Andre · Chicago</div><div className="ptp-ticker-type">Digital Product</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Scroll Stopper Studio</div><div className="ptp-ticker-builder">Keisha · Los Angeles</div><div className="ptp-ticker-type">Brick and Mortar</div></div>
        </div>

        <div className="ptp-ticker-row ptp-reverse">
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">The Purity Culture Recovery Guide</div><div className="ptp-ticker-builder">Simone · Nashville</div><div className="ptp-ticker-type">Digital Product</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Kingdom Finances Bootcamp</div><div className="ptp-ticker-builder">James · Miami</div><div className="ptp-ticker-type">Course</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Faithful Founders Community</div><div className="ptp-ticker-builder">Naomi · DC</div><div className="ptp-ticker-type">Community</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Spirit Led Therapy Directory</div><div className="ptp-ticker-builder">Dr. Aaliyah · Atlanta</div><div className="ptp-ticker-type">SaaS</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">The Creator House ATL</div><div className="ptp-ticker-builder">Jordan · Atlanta</div><div className="ptp-ticker-type">Brick and Mortar</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Word and Work Retreat</div><div className="ptp-ticker-builder">Pastor Eli · Raleigh</div><div className="ptp-ticker-type">Popup Event</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">The Purity Culture Recovery Guide</div><div className="ptp-ticker-builder">Simone · Nashville</div><div className="ptp-ticker-type">Digital Product</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Kingdom Finances Bootcamp</div><div className="ptp-ticker-builder">James · Miami</div><div className="ptp-ticker-type">Course</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Faithful Founders Community</div><div className="ptp-ticker-builder">Naomi · DC</div><div className="ptp-ticker-type">Community</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Spirit Led Therapy Directory</div><div className="ptp-ticker-builder">Dr. Aaliyah · Atlanta</div><div className="ptp-ticker-type">SaaS</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">The Creator House ATL</div><div className="ptp-ticker-builder">Jordan · Atlanta</div><div className="ptp-ticker-type">Brick and Mortar</div></div>
          <div className="ptp-ticker-item"><div className="ptp-ticker-dot"></div><div className="ptp-ticker-product">Word and Work Retreat</div><div className="ptp-ticker-builder">Pastor Eli · Raleigh</div><div className="ptp-ticker-type">Popup Event</div></div>
        </div>

      </div>

      <div className="ptp-ticker-stats">
        <div className="ptp-stat">
          <span className="ptp-stat-num">7</span>
          <span className="ptp-stat-label">days</span>
        </div>
        <div className="ptp-stat">
          <span className="ptp-stat-num">1</span>
          <span className="ptp-stat-label">live page</span>
        </div>
        <div className="ptp-stat">
          <span className="ptp-stat-num">∞</span>
          <span className="ptp-stat-label">possibilities</span>
        </div>
      </div>
    </section>
  );
};

// Inside the App Section - 7 Day Cards
const InsideTheAppSection = () => {
  return (
    <div className="inside-app-section" data-testid="inside-app-section">
      <style>{`
        .ptp-section-header {
          text-align: center;
          padding: 0 24px;
          margin-bottom: 48px;
        }
        .ptp-pill {
          display: inline-block;
          background: rgba(212,175,55,0.08);
          color: #d4af37;
          border: 1px solid rgba(212,175,55,0.18);
          border-radius: 20px;
          padding: 5px 16px;
          font-size: 0.62rem;
          letter-spacing: 2px;
          text-transform: uppercase;
          font-weight: 600;
          margin-bottom: 16px;
        }
        .ptp-section-header h2 {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(1.6rem, 3vw, 2.4rem);
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 8px;
          letter-spacing: -0.5px;
        }
        .ptp-section-header p {
          font-size: 0.9rem;
          color: rgba(255,255,255,0.4);
          font-style: italic;
        }
        .ptp-track-wrap {
          overflow-x: auto;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
          padding: 8px 48px 32px;
          cursor: grab;
        }
        .ptp-track-wrap:active { cursor: grabbing; }
        .ptp-track-wrap::-webkit-scrollbar { display: none; }
        .ptp-track {
          display: flex;
          gap: 18px;
          width: max-content;
        }
        .ptp-card {
          width: 268px;
          border-radius: 22px;
          border: 1px solid rgba(212,175,55,0.18);
          background: #111009;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
          flex-shrink: 0;
        }
        .ptp-card:hover {
          transform: translateY(-8px);
          border-color: rgba(212,175,55,0.42);
          box-shadow: 0 28px 64px rgba(0,0,0,0.65), 0 0 0 1px rgba(212,175,55,0.08);
        }
        .ptp-card-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 13px 16px 11px;
          border-bottom: 1px solid rgba(255,255,255,0.04);
          flex-shrink: 0;
        }
        .ptp-card-day {
          font-family: 'Cormorant Garamond', serif;
          font-size: 0.65rem;
          font-weight: 800;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: #d4af37;
        }
        .ptp-card-tag {
          font-size: 0.55rem;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.22);
          font-weight: 600;
        }
        .ptp-card-body {
          flex: 1;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          min-height: 0;
        }
        .ptp-card-foot {
          padding: 11px 16px 15px;
          border-top: 1px solid rgba(212,175,55,0.07);
          flex-shrink: 0;
        }
        .ptp-card-outcome {
          font-size: 0.72rem;
          color: rgba(255,255,255,0.45);
          line-height: 1.55;
          font-style: italic;
        }
        .ptp-card-outcome strong {
          color: #ffffff;
          font-style: normal;
          font-weight: 600;
        }
        .ptp-lbl {
          font-size: 0.54rem;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: rgba(212,175,55,0.45);
          font-weight: 700;
        }
        .ptp-gold-line { height: 1px; background: rgba(212,175,55,0.1); }
        .ptp-reveal {
          background: rgba(212,175,55,0.07);
          border: 1px solid rgba(212,175,55,0.2);
          border-radius: 12px;
          padding: 11px 13px;
          display: flex;
          gap: 8px;
          align-items: flex-start;
          margin-top: auto;
        }
        .ptp-reveal-icon {
          width: 20px; height: 20px;
          border-radius: 50%;
          background: rgba(212,175,55,0.14);
          display: flex; align-items: center; justify-content: center;
          font-size: 0.58rem;
          color: #d4af37;
          flex-shrink: 0;
          margin-top: 1px;
        }
        .ptp-reveal-lbl {
          font-size: 0.5rem;
          letter-spacing: 1.8px;
          text-transform: uppercase;
          color: #d4af37;
          font-weight: 700;
          margin-bottom: 3px;
        }
        .ptp-reveal-txt {
          font-size: 0.77rem;
          font-weight: 700;
          color: #ffffff;
          line-height: 1.35;
          font-family: 'Cormorant Garamond', serif;
        }
        .ptp-matrix { display: flex; flex-direction: column; gap: 5px; }
        .ptp-mhead { display: grid; grid-template-columns: 14px 1fr 1fr; gap: 4px; }
        .ptp-mh { font-size: 0.5rem; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(255,255,255,0.18); font-weight: 700; }
        .ptp-mrow { display: grid; grid-template-columns: 14px 1fr 1fr; gap: 4px; align-items: stretch; }
        .ptp-mnum { font-size: 0.55rem; color: rgba(212,175,55,0.35); font-weight: 700; padding-top: 7px; text-align: center; }
        .ptp-mcell { background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.05); border-radius: 7px; padding: 6px 7px; font-size: 0.62rem; color: rgba(255,255,255,0.5); line-height: 1.4; }
        .ptp-mcell.on { background: rgba(212,175,55,0.05); border-color: rgba(212,175,55,0.16); color: rgba(255,255,255,0.72); }
        .ptp-score-num { font-family: 'Cormorant Garamond', serif; font-style: italic; font-size: 3.6rem; font-weight: 700; color: #d4af37; line-height: 1; letter-spacing: -1px; }
        .ptp-score-denom { font-size: 1.3rem; color: rgba(212,175,55,0.28); font-weight: 400; font-style: normal; }
        .ptp-score-bar { height: 3px; background: rgba(212,175,55,0.1); border-radius: 2px; overflow: hidden; margin: 5px 0 3px; }
        .ptp-score-fill { height: 100%; background: linear-gradient(90deg, #d4af37, #f0c84a); border-radius: 2px; }
        .ptp-score-sub { font-size: 0.56rem; color: rgba(255,255,255,0.22); letter-spacing: 1px; text-transform: uppercase; }
        .ptp-quote { font-style: italic; font-size: 0.8rem; color: rgba(255,255,255,0.55); line-height: 1.6; border-left: 2px solid rgba(212,175,55,0.28); padding-left: 10px; }
        .ptp-quote strong { color: #ffffff; font-style: normal; }
        .ptp-top-idea { background: rgba(212,175,55,0.07); border: 1px solid rgba(212,175,55,0.2); border-radius: 12px; padding: 12px 12px 10px; }
        .ptp-ti-meta { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
        .ptp-ti-badge { width: 19px; height: 19px; background: #d4af37; border-radius: 5px; display: flex; align-items: center; justify-content: center; font-size: 0.6rem; font-weight: 900; color: #080706; flex-shrink: 0; }
        .ptp-ti-lbl { font-size: 0.5rem; color: rgba(212,175,55,0.55); letter-spacing: 1.5px; text-transform: uppercase; font-weight: 700; }
        .ptp-ti-score { margin-left: auto; font-family: 'Cormorant Garamond', serif; font-size: 0.85rem; font-weight: 800; color: #d4af37; }
        .ptp-ti-name { font-family: 'Cormorant Garamond', serif; font-size: 0.85rem; font-weight: 700; color: #ffffff; line-height: 1.2; margin-bottom: 3px; }
        .ptp-ti-desc { font-size: 0.62rem; color: rgba(255,255,255,0.4); line-height: 1.4; }
        .ptp-others { display: flex; flex-direction: column; gap: 4px; }
        .ptp-orow { display: flex; align-items: center; gap: 7px; padding: 6px 9px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); border-radius: 8px; }
        .ptp-on { font-size: 0.58rem; color: rgba(255,255,255,0.18); font-weight: 700; width: 10px; }
        .ptp-oname { font-size: 0.63rem; color: rgba(255,255,255,0.38); flex: 1; }
        .ptp-oscore { font-size: 0.6rem; color: rgba(212,175,55,0.32); font-weight: 700; }
        .ptp-mvp-cols { display: flex; gap: 6px; flex: 1; align-items: stretch; }
        .ptp-mvp-col { flex: 1; border-radius: 10px; padding: 10px 8px; display: flex; flex-direction: column; gap: 6px; border: 1px solid rgba(255,255,255,0.05); background: rgba(255,255,255,0.02); min-width: 0; }
        .ptp-mvp-col.win { background: rgba(74,222,128,0.06); border-color: rgba(74,222,128,0.24); }
        .ptp-mvp-lbl { font-size: 0.52rem; letter-spacing: 1px; text-transform: uppercase; color: rgba(255,255,255,0.2); font-weight: 700; line-height: 1.4; }
        .ptp-mvp-col.win .ptp-mvp-lbl { color: #4ade80; }
        .ptp-mvp-dot { width: 6px; height: 6px; border-radius: 50%; background: rgba(255,255,255,0.08); }
        .ptp-mvp-col.win .ptp-mvp-dot { background: #4ade80; }
        .ptp-mvp-item { font-size: 0.6rem; color: rgba(255,255,255,0.28); line-height: 1.45; padding-left: 7px; border-left: 1px solid rgba(255,255,255,0.06); word-break: break-word; }
        .ptp-mvp-col.win .ptp-mvp-item { color: rgba(255,255,255,0.6); border-left-color: rgba(74,222,128,0.2); }
        .ptp-mvp-arrow { display: flex; align-items: center; justify-content: center; color: rgba(212,175,55,0.4); font-size: 0.75rem; flex-shrink: 0; padding-top: 28px; }
        .ptp-copy-wrap { background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05); border-radius: 12px; padding: 13px; flex: 1; display: flex; flex-direction: column; gap: 8px; }
        .ptp-copy-hl { font-family: 'Cormorant Garamond', serif; font-size: 1rem; font-weight: 700; color: #ffffff; line-height: 1.2; }
        .ptp-copy-hl span { color: #d4af37; }
        .ptp-copy-sub { font-size: 0.65rem; color: rgba(255,255,255,0.4); line-height: 1.55; }
        .ptp-benefit { border-left: 2px solid rgba(212,175,55,0.18); padding-left: 8px; }
        .ptp-bt { font-size: 0.65rem; font-weight: 700; color: rgba(255,255,255,0.62); margin-bottom: 1px; }
        .ptp-bd { font-size: 0.58rem; color: rgba(255,255,255,0.26); line-height: 1.4; }
        .ptp-copy-cta { background: #d4af37; color: #080706; font-family: 'Cormorant Garamond', serif; font-size: 0.63rem; font-weight: 700; padding: 8px 12px; border-radius: 8px; text-align: center; margin-top: auto; flex-shrink: 0; }
        .ptp-browser { background: #0d0b09; border: 1px solid rgba(212,175,55,0.14); border-radius: 12px; overflow: hidden; flex: 1; display: flex; flex-direction: column; }
        .ptp-b-bar { background: #1a1612; padding: 7px 10px; display: flex; align-items: center; gap: 4px; border-bottom: 1px solid rgba(255,255,255,0.04); flex-shrink: 0; }
        .ptp-bdot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
        .ptp-burl { font-size: 0.52rem; color: rgba(255,255,255,0.16); margin-left: 7px; }
        .ptp-b-body { padding: 12px 13px; display: flex; flex-direction: column; gap: 6px; flex: 1; }
        .ptp-b-pill { display: inline-block; background: rgba(212,175,55,0.1); color: #d4af37; font-size: 0.48rem; padding: 2px 7px; border-radius: 20px; border: 1px solid rgba(212,175,55,0.18); letter-spacing: 1px; text-transform: uppercase; width: fit-content; }
        .ptp-b-hl { font-family: 'Cormorant Garamond', serif; font-size: 0.9rem; font-weight: 700; color: #ffffff; line-height: 1.2; }
        .ptp-b-hl span { color: #d4af37; }
        .ptp-b-sub { font-size: 0.58rem; color: rgba(255,255,255,0.32); line-height: 1.5; }
        .ptp-b-cta { background: #d4af37; color: #080706; font-family: 'Cormorant Garamond', serif; font-size: 0.56rem; font-weight: 700; padding: 5px 10px; border-radius: 5px; width: fit-content; }
        .ptp-live-strip { display: flex; align-items: center; gap: 6px; padding: 7px 13px; border-top: 1px solid rgba(74,222,128,0.08); background: rgba(74,222,128,0.04); flex-shrink: 0; }
        .ptp-live-dot { width: 5px; height: 5px; border-radius: 50%; background: #4ade80; animation: ptpPulse 1.5s infinite; }
        @keyframes ptpPulse { 0%,100%{opacity:1;} 50%{opacity:0.4;} }
        .ptp-live-lbl { font-size: 0.58rem; color: #4ade80; font-weight: 600; }
        .ptp-live-url { font-size: 0.52rem; color: rgba(255,255,255,0.15); margin-left: auto; }
        .ptp-convos { display: flex; flex-direction: column; gap: 4px; }
        .ptp-convo { display: flex; align-items: center; gap: 7px; padding: 6px 8px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04); border-radius: 8px; }
        .ptp-cav { width: 20px; height: 20px; border-radius: 50%; background: rgba(212,175,55,0.08); border: 1px solid rgba(212,175,55,0.14); font-size: 0.55rem; font-weight: 700; display: flex; align-items: center; justify-content: center; color: #d4af37; flex-shrink: 0; }
        .ptp-cname { font-size: 0.63rem; font-weight: 600; color: rgba(255,255,255,0.48); }
        .ptp-cquote { font-size: 0.56rem; color: rgba(255,255,255,0.22); line-height: 1.3; font-style: italic; }
        .ptp-cyes { margin-left: auto; font-size: 0.54rem; font-weight: 700; color: #4ade80; flex-shrink: 0; letter-spacing: 0.5px; }
        .ptp-cmaybe { color: #facc15; }
        .ptp-go-box { background: rgba(74,222,128,0.07); border: 1px solid rgba(74,222,128,0.26); border-radius: 12px; padding: 11px 12px; display: flex; align-items: center; gap: 9px; margin-top: 4px; }
        .ptp-go-dot { width: 9px; height: 9px; border-radius: 50%; background: #4ade80; box-shadow: 0 0 8px rgba(74,222,128,0.5); flex-shrink: 0; }
        .ptp-go-lbl { font-family: 'Cormorant Garamond', serif; font-size: 0.78rem; font-weight: 800; color: #ffffff; }
        .ptp-go-sub { font-size: 0.58rem; color: rgba(255,255,255,0.32); margin-top: 1px; }
        .ptp-go-check { margin-left: auto; width: 18px; height: 18px; background: #4ade80; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.58rem; color: #080706; font-weight: 900; flex-shrink: 0; }
      `}</style>

      <div className="ptp-track-wrap">
        <div className="ptp-track">

          {/* Day 1 Card */}
          <div className="ptp-card">
            <div className="ptp-card-bar">
              <div className="ptp-card-day">Day 1</div>
            </div>
            <div className="ptp-card-body">
              <div className="ptp-lbl">Pain Point Matrix</div>
              <div className="ptp-matrix">
                <div className="ptp-mhead">
                  <div></div>
                  <div className="ptp-mh">Problem</div>
                  <div className="ptp-mh">Who</div>
                </div>
                <div className="ptp-mrow">
                  <div className="ptp-mnum">1</div>
                  <div className="ptp-mcell on">Creators with audiences but no products</div>
                  <div className="ptp-mcell on">Faith creators 1k to 50k</div>
                </div>
                <div className="ptp-mrow">
                  <div className="ptp-mnum">2</div>
                  <div className="ptp-mcell">No faith learning platform</div>
                  <div className="ptp-mcell">Christian millennials</div>
                </div>
                <div className="ptp-mrow">
                  <div className="ptp-mnum">3</div>
                  <div className="ptp-mcell">Online community, no IRL</div>
                  <div className="ptp-mcell">Followers craving real rooms</div>
                </div>
              </div>
              <div className="ptp-reveal">
                <div className="ptp-reveal-icon">◎</div>
                <div>
                  <div className="ptp-reveal-lbl">Your Calling</div>
                  <div className="ptp-reveal-txt">Monetize a faith audience through digital products and live experiences</div>
                </div>
              </div>
            </div>
            <div className="ptp-card-foot">
              <div className="ptp-card-outcome">You leave knowing <strong>the one problem you are built to solve.</strong></div>
            </div>
          </div>

          {/* Day 2 Card */}
          <div className="ptp-card">
            <div className="ptp-card-bar">
              <div className="ptp-card-day">Day 2</div>
              <div className="ptp-card-tag">Strengths</div>
            </div>
            <div className="ptp-card-body">
              <div className="ptp-lbl">Founder Market Fit</div>
              <div>
                <div className="ptp-score-num">9.2<span className="ptp-score-denom">/10</span></div>
                <div className="ptp-score-bar"><div className="ptp-score-fill" style={{width:'92%'}}></div></div>
                <div className="ptp-score-sub">Founder Market Fit Score</div>
              </div>
              <div className="ptp-gold-line"></div>
              <div className="ptp-quote">&ldquo;I can build the audience <strong>and</strong> the product <strong>and</strong> the room. Most people can only do one.&rdquo;</div>
              <div className="ptp-reveal" style={{marginTop:'auto'}}>
                <div className="ptp-reveal-icon">⚡</div>
                <div>
                  <div className="ptp-reveal-lbl">Why You Win</div>
                  <div className="ptp-reveal-txt">No one else has both the content muscle and community infrastructure.</div>
                </div>
              </div>
            </div>
            <div className="ptp-card-foot">
              <div className="ptp-card-outcome">You leave with <strong>proof you are the right person to build it.</strong></div>
            </div>
          </div>

          {/* Day 3 Card */}
          <div className="ptp-card">
            <div className="ptp-card-bar">
              <div className="ptp-card-day">Day 3</div>
              <div className="ptp-card-tag">Ideas</div>
            </div>
            <div className="ptp-card-body">
              <div className="ptp-lbl">Your Ranked Ideas</div>
              <div className="ptp-top-idea">
                <div className="ptp-ti-meta">
                  <div className="ptp-ti-badge">1</div>
                  <div className="ptp-ti-lbl">Top Pick</div>
                  <div className="ptp-ti-score">9.2</div>
                </div>
                <div className="ptp-ti-name">Creator to Course Blueprint</div>
                <div className="ptp-ti-desc">PDF guide and mini course. Launch your first digital product in 30 days.</div>
              </div>
              <div className="ptp-others">
                <div className="ptp-orow"><div className="ptp-on">2</div><div className="ptp-oname">Kingdom Creators Community</div><div className="ptp-oscore">8.8</div></div>
                <div className="ptp-orow"><div className="ptp-on">3</div><div className="ptp-oname">The Scroll Stopper Popup</div><div className="ptp-oscore">8.4</div></div>
                <div className="ptp-orow"><div className="ptp-on">4</div><div className="ptp-oname">Faith and Filter App</div><div className="ptp-oscore">7.9</div></div>
                <div className="ptp-orow"><div className="ptp-on">5</div><div className="ptp-oname">The Creator House</div><div className="ptp-oscore">7.2</div></div>
              </div>
            </div>
            <div className="ptp-card-foot">
              <div className="ptp-card-outcome">You leave with <strong>one committed idea. Not a list.</strong></div>
            </div>
          </div>

          {/* Day 4 Card */}
          <div className="ptp-card">
            <div className="ptp-card-bar">
              <div className="ptp-card-day">Day 4</div>
              <div className="ptp-card-tag">MVP Scope</div>
            </div>
            <div className="ptp-card-body">
              <div className="ptp-lbl">Your Right-Sized MVP</div>
              <div className="ptp-mvp-cols">
                <div className="ptp-mvp-col">
                  <div className="ptp-mvp-dot"></div>
                  <div className="ptp-mvp-lbl">Too Big</div>
                  <div className="ptp-mvp-item">Full platform</div>
                  <div className="ptp-mvp-item">Live coaching</div>
                  <div className="ptp-mvp-item">Custom app</div>
                  <div className="ptp-mvp-item">Payments</div>
                </div>
                <div className="ptp-mvp-arrow">›</div>
                <div className="ptp-mvp-col">
                  <div className="ptp-mvp-dot"></div>
                  <div className="ptp-mvp-lbl">Still Big</div>
                  <div className="ptp-mvp-item">Website</div>
                  <div className="ptp-mvp-item">5 modules</div>
                  <div className="ptp-mvp-item">Community</div>
                  <div className="ptp-mvp-item">Emails</div>
                </div>
                <div className="ptp-mvp-arrow">›</div>
                <div className="ptp-mvp-col win">
                  <div className="ptp-mvp-dot"></div>
                  <div className="ptp-mvp-lbl">Ships Now</div>
                  <div className="ptp-mvp-item">PDF guide</div>
                  <div className="ptp-mvp-item">Landing page</div>
                  <div className="ptp-mvp-item">Checkout</div>
                  <div className="ptp-mvp-item">3 emails</div>
                </div>
              </div>
            </div>
            <div className="ptp-card-foot">
              <div className="ptp-card-outcome">You leave knowing <strong>exactly what ships first.</strong></div>
            </div>
          </div>

          {/* Day 5 Card */}
          <div className="ptp-card">
            <div className="ptp-card-bar">
              <div className="ptp-card-day">Day 5</div>
              <div className="ptp-card-tag">Copy</div>
            </div>
            <div className="ptp-card-body">
              <div className="ptp-lbl">Generated Copy</div>
              <div className="ptp-copy-wrap">
                <div className="ptp-copy-hl">You Have the Audience.<br/><span>Now Build the Offer.</span></div>
                <div className="ptp-copy-sub">Turn your following into a product that sells in 30 days.</div>
                <div className="ptp-gold-line"></div>
                <div className="ptp-benefit">
                  <div className="ptp-bt">Stop leaving money in the DMs.</div>
                  <div className="ptp-bd">Package what you know into an offer they will actually buy.</div>
                </div>
                <div className="ptp-benefit">
                  <div className="ptp-bt">Built for more than the algorithm.</div>
                  <div className="ptp-bd">Values-aligned. Clear path to income. No compromise.</div>
                </div>
                <div className="ptp-copy-cta">Get the Blueprint for $37</div>
              </div>
            </div>
            <div className="ptp-card-foot">
              <div className="ptp-card-outcome">You leave with <strong>copy written in your voice, from your story.</strong></div>
            </div>
          </div>

          {/* Day 6 Card */}
          <div className="ptp-card">
            <div className="ptp-card-bar">
              <div className="ptp-card-day">Day 6</div>
              <div className="ptp-card-tag">Live Page</div>
            </div>
            <div className="ptp-card-body">
              <div className="ptp-lbl">Live Preview</div>
              <div className="ptp-browser">
                <div className="ptp-b-bar">
                  <div className="ptp-bdot" style={{background:'#ff5f57'}}></div>
                  <div className="ptp-bdot" style={{background:'#febc2e'}}></div>
                  <div className="ptp-bdot" style={{background:'#28c840'}}></div>
                  <div className="ptp-burl">ptp.co/jordan</div>
                </div>
                <div className="ptp-b-body">
                  <div className="ptp-b-pill">Faith Creators</div>
                  <div className="ptp-b-hl"><span>You Have the Audience.</span><br/>Now Build the Offer.</div>
                  <div className="ptp-b-sub">Turn your content into a product that sells, starting today.</div>
                  <div className="ptp-b-cta">Get the Blueprint</div>
                </div>
                <div className="ptp-live-strip">
                  <div className="ptp-live-dot"></div>
                  <div className="ptp-live-lbl">Live right now</div>
                  <div className="ptp-live-url">ptp.co/jordan</div>
                </div>
              </div>
            </div>
            <div className="ptp-card-foot">
              <div className="ptp-card-outcome">You leave with <strong>a real page you can send today.</strong></div>
            </div>
          </div>

          {/* Day 7 Card */}
          <div className="ptp-card">
            <div className="ptp-card-bar">
              <div className="ptp-card-day">Day 7</div>
              <div className="ptp-card-tag">Decision</div>
            </div>
            <div className="ptp-card-body">
              <div className="ptp-lbl">5 Conversations Done</div>
              <div className="ptp-convos">
                <div className="ptp-convo">
                  <div className="ptp-cav">A</div>
                  <div style={{flex:1,minWidth:0}}><div className="ptp-cname">Aaliyah</div><div className="ptp-cquote">&ldquo;I have been waiting for this.&rdquo;</div></div>
                  <div className="ptp-cyes">YES</div>
                </div>
                <div className="ptp-convo">
                  <div className="ptp-cav">C</div>
                  <div style={{flex:1,minWidth:0}}><div className="ptp-cname">Chris</div><div className="ptp-cquote">&ldquo;$37 is nothing if it helps me launch.&rdquo;</div></div>
                  <div className="ptp-cyes">YES</div>
                </div>
                <div className="ptp-convo">
                  <div className="ptp-cav">D</div>
                  <div style={{flex:1,minWidth:0}}><div className="ptp-cname">Destiny</div><div className="ptp-cquote">&ldquo;I need structure more than info.&rdquo;</div></div>
                  <div className="ptp-cyes">YES</div>
                </div>
                <div className="ptp-convo">
                  <div className="ptp-cav">T</div>
                  <div style={{flex:1,minWidth:0}}><div className="ptp-cname">Tre</div><div className="ptp-cquote">&ldquo;Show me the workbook first.&rdquo;</div></div>
                  <div className="ptp-cyes ptp-cmaybe">MAYBE</div>
                </div>
                <div className="ptp-convo">
                  <div className="ptp-cav">N</div>
                  <div style={{flex:1,minWidth:0}}><div className="ptp-cname">Naomi</div><div className="ptp-cquote">&ldquo;Said I needed this last week.&rdquo;</div></div>
                  <div className="ptp-cyes">YES</div>
                </div>
              </div>
              <div className="ptp-go-box">
                <div className="ptp-go-dot"></div>
                <div style={{minWidth:0}}>
                  <div className="ptp-go-lbl">GO</div>
                  <div className="ptp-go-sub">4 of 5 said yes. Launching this week.</div>
                </div>
                <div className="ptp-go-check">✓</div>
              </div>
            </div>
            <div className="ptp-card-foot">
              <div className="ptp-card-outcome">You leave with <strong>a real decision backed by real people.</strong></div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};



// 7-Day Breakdown Section
const SevenDayBreakdown = () => {
  const days = [
    { day: 1, title: "Pain Point Matrix", description: "You leave with clarity on the one problem you keep coming back to, and why it is the right one to build around. This is your foundation. Everything else sits on top of it." },
    { day: 2, title: "Your Unfair Advantage Statement", description: "You leave with a written statement that explains exactly why you, with your specific background, story, and experience, are the right person to build this. Not generic. Not motivational. True." },
    { day: 3, title: "One Committed Idea", description: "You leave with one ranked, chosen business idea. Not a shortlist. Not a maybe. One idea you have committed to building, with a score showing exactly why it fits you." },
    { day: 4, title: "A Scoped MVP", description: "You leave knowing exactly what version one looks like. What is in, what is out, and what ships first. No more building everything before anything exists." },
    { day: 5, title: "Full Landing Page Copy", description: "You leave with a complete set of copy written in your voice, drawn from your story. Headline, subheadline, benefits, founder section, FAQs. Ready to publish." },
    { day: 6, title: "A Live Landing Page", description: "You leave with a real, shareable page you can send to people today. No coding. No designer. Built, previewed, and published inside the sprint in one session." },
    { day: 7, title: "A Real Decision", description: "You leave with a GO, PIVOT, or RELEASE verdict backed by real conversations with real people. Not a gut feeling. Evidence. And a clear next step either way." }
  ];

  return (
    <section id="sprint-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0a0908]" data-testid="seven-day-breakdown">
      <div className="max-w-4xl mx-auto">
        {/* Section Label */}
        <div className="text-center mb-12">
          <span 
            className="inline-block mb-4"
            style={{
              background: 'rgba(212,175,55,0.13)',
              color: '#d4af37',
              border: '1px solid rgba(212,175,55,0.22)',
              borderRadius: '20px',
              padding: '5px 16px',
              fontSize: '0.68rem',
              letterSpacing: '1.5px',
              textTransform: 'uppercase'
            }}
          >
            THE SPRINT
          </span>
          <h2 className="text-white text-2xl sm:text-3xl font-bold mb-4">
            7 days. Here&apos;s the plan.
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.55)', maxWidth: '500px', margin: '0 auto' }}>
            Each day ends with something real. Not notes. Not ideas. A finished output that builds on the last.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div 
            className="absolute left-4 md:left-6 top-0 bottom-0 w-0.5"
            style={{
              background: 'linear-gradient(to bottom, #d4af37, rgba(212,175,55,0.1))'
            }}
          />

          {/* Timeline Items */}
          <div className="space-y-6">
            {days.map((item, index) => (
              <div key={item.day} className="relative pl-12 md:pl-16" data-testid={`day-${item.day}-item`}>
                {/* Node */}
                <div 
                  className="absolute left-0 md:left-2 w-8 h-8 rounded-full flex items-center justify-center"
                  style={{
                    background: index === 0 ? '#d4af37' : 'transparent',
                    border: `2px solid #d4af37`,
                    color: index === 0 ? '#0a0908' : '#d4af37',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                >
                  {item.day}
                </div>

                {/* Content */}
                <div className="pb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-white font-bold">
                      Day {item.day} — {item.title}
                    </h3>
                  </div>
                  <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, fontSize: '0.95rem' }}>
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Founder Story Section
const FounderStorySection = () => (
  <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0a0908]" data-testid="founder-story-section">
    <div className="max-w-4xl mx-auto">
      <div 
        className="flex flex-col md:flex-row gap-10 items-start p-[56px] rounded-[20px]"
        style={{
          background: 'linear-gradient(135deg, rgba(212,175,55,0.07) 0%, rgba(212,175,55,0.02) 100%)',
          border: '1px solid rgba(212,175,55,0.15)'
        }}
      >
        {/* Photo */}
        <div className="flex-shrink-0">
          <div 
            className="w-[160px] h-[160px] rounded-full overflow-hidden mx-auto md:mx-0"
            style={{ border: '3px solid #d4af37' }}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_b66a4579-8af7-4a3e-b266-aae2626c0025/artifacts/cwl31rlx_74FF7765-85BA-4FFB-B493-661C53B4E9CF.JPG" 
              alt="Nashae Roundtree"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Text Content */}
        <div className="flex-1">
          <h2 
            className="text-white font-bold mb-6"
            style={{ fontSize: '1.65rem', lineHeight: 1.3 }}
          >
            Built by someone who sat on her own idea for 18 months.
          </h2>

          <div 
            className="space-y-4 mb-6"
            style={{
              color: 'rgba(255,255,255,0.72)',
              fontSize: '0.93rem',
              lineHeight: 1.9
            }}
          >
            <p>
              I have a Harvard MBA. I am a Techstars-backed founder. I build AI products professionally at a Fortune 500 bank.
            </p>
            <p>
              I had every credential, every framework, every reason to start.
            </p>
            <p>
              And I still spent 18 months calling fear patience.
            </p>
            <p>
              I told myself I was praying about it. I was. But I was also hiding.
            </p>
          </div>

          <p className="text-white italic mb-6" style={{ lineHeight: 1.8 }}>
            Prayer to Prototype is the sprint I wish had existed. The one that would have told me: your idea is already validated. Your story is already your unfair advantage. Done is holy too.
          </p>

          <p className="text-white font-medium mb-6">
            You do not need more clarity. You need a forcing function.
          </p>

          {/* Credential Tags */}
          <div className="flex flex-wrap gap-2">
            {["Harvard MBA", "Techstars Founder", "AI Product Manager", "Fortune 500 Leader", "Speaker, BCI 2026"].map((credential) => (
              <span 
                key={credential}
                className="px-3 py-1 rounded-full text-sm"
                style={{
                  background: 'rgba(212,175,55,0.15)',
                  color: '#d4af37',
                  border: '1px solid rgba(212,175,55,0.3)'
                }}
              >
                {credential}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Scripture Rotation Section
const ScriptureRotationSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const scriptures = [
    { text: "Commit your works to the Lord, and your thoughts will be established.", ref: "Proverbs 16:3" },
    { text: "Faith by itself, if it has no works, is dead.", ref: "James 2:17" },
    { text: "God has not given us a spirit of fear and timidity, but of power, love, and self-discipline.", ref: "2 Timothy 1:7" },
    { text: "Let each of you look not to your own interests, but to the interests of others.", ref: "Philippians 2:4" },
    { text: "Each of you should use whatever gift you have received to serve others.", ref: "1 Peter 4:10" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % scriptures.length);
        setIsVisible(true);
      }, 800);
    }, 5600); // 0.8s fade out + 4s hold + 0.8s fade in

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-transparent" data-testid="scripture-rotation">
      <div 
        className="max-w-[680px] mx-auto transition-opacity duration-[800ms]"
        style={{ opacity: isVisible ? 1 : 0 }}
      >
        <blockquote 
          className="text-left"
          style={{
            borderLeft: '3px solid #d4af37',
            paddingLeft: '28px'
          }}
        >
          <p 
            style={{
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
              color: '#d4af37',
              fontSize: '1.4rem',
              lineHeight: 1.8
            }}
          >
            &ldquo;{scriptures[currentIndex].text}&rdquo; — {scriptures[currentIndex].ref}
          </p>
        </blockquote>
      </div>
    </section>
  );
};

// Apply Section - Replaces Bottom CTA with modal popup
const ApplySection = () => {
  const [applyForm, setApplyForm] = useState({ first_name: "", email: "", idea_description: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/admin/waitlist/submit`, applyForm);
      setShowModal(true);
    } catch (err) {
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setApplyForm({ first_name: "", email: "", idea_description: "" });
  };

  return (
    <>
      {/* Success Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.85)' }}
          onClick={closeModal}
        >
          <div 
            className="relative max-w-md w-full p-8 rounded-2xl"
            style={{
              background: '#111009',
              border: '1px solid rgba(212,175,55,0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
              data-testid="modal-close-btn"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ background: 'rgba(212,175,55,0.15)', border: '2px solid rgba(212,175,55,0.4)' }}
              >
                <Check className="w-8 h-8 text-[#d4af37]" />
              </div>
              
              <h2 
                className="text-white font-bold mb-2"
                style={{ fontSize: '1.8rem', fontFamily: 'Cormorant Garamond, Georgia, serif' }}
              >
                You&apos;re in.
              </h2>
              
              <p className="text-[#d4af37] text-lg mb-4">
                Application received.
              </p>
              
              <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, marginBottom: '1.5rem' }}>
                I&apos;m personally reviewing every application. You&apos;ll hear back within a week. In the meantime, keep building.
              </p>

              <button
                onClick={closeModal}
                className="px-8 py-3 rounded-full font-semibold transition-all hover:opacity-90"
                style={{
                  background: '#d4af37',
                  color: '#0a0908'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Section */}
      <section id="apply-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0a0908]" data-testid="apply-section">
        <div className="max-w-xl mx-auto">
          {/* Section Heading */}
          <div className="text-center mb-10">
            <span 
              className="inline-block mb-4"
              style={{
                background: 'rgba(212,175,55,0.13)',
                color: '#d4af37',
                border: '1px solid rgba(212,175,55,0.22)',
                borderRadius: '20px',
                padding: '5px 16px',
                fontSize: '0.68rem',
                letterSpacing: '1.5px',
                textTransform: 'uppercase'
              }}
            >
              APPLICATIONS OPEN
            </span>
            <h2 className="text-white text-2xl sm:text-3xl font-bold mb-3">
              Ready to build what you&apos;ve been praying about?
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.95rem' }}>
              20 spots. I personally review every application. You&apos;ll hear back within a week.
            </p>
          </div>

          {/* Application Form */}
          <form onSubmit={handleApplySubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                value={applyForm.first_name}
                onChange={(e) => setApplyForm({...applyForm, first_name: e.target.value})}
                placeholder="First name"
                required
                className="bg-[#111009] border-white/10 text-white placeholder:text-white/30 py-6"
                data-testid="apply-firstname"
              />
              <Input
                type="email"
                value={applyForm.email}
                onChange={(e) => setApplyForm({...applyForm, email: e.target.value})}
                placeholder="Email address"
                required
                className="bg-[#111009] border-white/10 text-white placeholder:text-white/30 py-6"
                data-testid="apply-email"
              />
            </div>
            <Textarea
              value={applyForm.idea_description}
              onChange={(e) => setApplyForm({...applyForm, idea_description: e.target.value})}
              placeholder="Tell us about your journey and why you want to join the next sprint."
              required
              className="bg-[#111009] border-white/10 text-white placeholder:text-white/30 resize-none h-28"
              data-testid="apply-idea"
            />
            <button 
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 rounded-full font-bold text-base transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-50"
              style={{
                background: '#d4af37',
                color: '#0a0908'
              }}
              data-testid="apply-submit-btn"
            >
              {isSubmitting ? "Submitting..." : "Apply for the Sprint"} {!isSubmitting && <ArrowRight className="w-4 h-4 ml-2 inline" />}
            </button>
          </form>
        </div>
      </section>
    </>
  );
};

// Footer
const Footer = () => (
  <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-[#0a0908] border-t border-white/5">
    <div className="max-w-6xl mx-auto text-center">
      <p style={{ fontFamily: 'Georgia, serif' }}>
        <span className="text-[#d4af37]">Prayer</span>
        <span className="text-white/70"> to </span>
        <span className="text-[#d4af37]">Prototype</span>
      </p>
      <p className="text-white/30 text-sm mt-2">
        © {new Date().getFullYear()} All rights reserved.
      </p>
    </div>
  </footer>
);

// Email Capture Gate Page
const EmailCaptureGate = ({ onBack, onSuccess }) => {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleAuth = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setIsLoading(true);
    try {
      // For email signup, we'll use test access for now
      // In production, this would send a magic link
      const response = await axios.post(`${API}/auth/test-access`, {
        email: email.trim(),
        name: email.split('@')[0]
      }, { withCredentials: true });
      
      toast.success("Account created! Redirecting...");
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1000);
    } catch (error) {
      console.error('Email signup error:', error);
      toast.error("Failed to create account. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0908] flex items-center justify-center px-4" data-testid="email-capture-gate">
      <div className="max-w-[520px] w-full text-center">
        <button 
          onClick={onBack}
          className="absolute top-6 left-6 text-white/40 hover:text-white/70 flex items-center gap-2 text-sm"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>

        {/* App Logo */}
        <div className="mb-8">
          <p style={{ fontFamily: 'Georgia, serif', fontSize: '1.5rem' }}>
            <span className="text-[#d4af37]">Prayer</span>
            <span className="text-white/70"> to </span>
            <span className="text-[#d4af37]">Prototype</span>
          </p>
        </div>

        <h1 
          className="text-white font-bold mb-4"
          style={{ fontSize: '2.2rem', lineHeight: 1.2 }}
        >
          Your God-given business idea is closer than you think.
        </h1>
        
        <p 
          className="mb-8"
          style={{
            fontStyle: 'italic',
            color: '#d4af37',
            fontSize: '1.1rem'
          }}
        >
          Start Day 1 free. Discover the problem you are built to solve.
        </p>

        {/* Auth Options */}
        <div className="space-y-3">
          <button
            onClick={handleGoogleAuth}
            className="w-full flex items-center gap-3 px-6 py-[15px] rounded-lg transition-all hover:border-[rgba(212,175,55,0.3)]"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: '#ffffff',
              fontSize: '0.95rem',
              fontWeight: 500
            }}
            data-testid="google-auth-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <button
            className="w-full flex items-center gap-3 px-6 py-[15px] rounded-lg transition-all hover:border-[rgba(212,175,55,0.3)] opacity-50 cursor-not-allowed"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: '#ffffff',
              fontSize: '0.95rem',
              fontWeight: 500
            }}
            disabled
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Continue with Apple
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4 my-4">
            <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem' }}>or</span>
            <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
          </div>

          {!showEmailForm ? (
            <button
              onClick={() => setShowEmailForm(true)}
              className="w-full flex items-center gap-3 px-6 py-[15px] rounded-lg transition-all hover:border-[rgba(212,175,55,0.3)]"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: '#ffffff',
                fontSize: '0.95rem',
                fontWeight: 500
              }}
              data-testid="email-auth-btn"
            >
              <Mail className="w-5 h-5" />
              Continue with Email
            </button>
          ) : (
            <form onSubmit={handleEmailSubmit} className="space-y-3">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full px-5 py-4 rounded-lg text-white"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '2px solid rgba(212,175,55,0.3)',
                  fontSize: '1rem'
                }}
                data-testid="email-input"
              />
              <Button
                type="submit"
                disabled={isLoading || !email.trim()}
                className="w-full py-[18px] rounded-lg font-bold text-base"
                style={{
                  background: '#d4af37',
                  color: '#0a0908'
                }}
                data-testid="email-submit-btn"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Creating account...
                  </span>
                ) : (
                  <>Begin Day 1 Free <ArrowRight className="w-4 h-4 ml-2 inline" /></>
                )}
              </Button>
            </form>
          )}
        </div>

        {/* Fine Print */}
        <p className="mt-6" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', textAlign: 'center' }}>
          No credit card. No obligation. Takes about 45 minutes.
        </p>
      </div>
    </div>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  const navigate = useNavigate();
  const [showGate, setShowGate] = useState(false);

  const handleSignIn = () => {
    // Use the internal auth gate instead of external redirect
    setShowGate(true);
  };

  if (showGate) {
    return (
      <EmailCaptureGate 
        onBack={() => setShowGate(false)}
        onSuccess={() => navigate('/dashboard')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0908]" data-testid="landing-page">
      {/* Section 1: Hero */}
      <Nav onSignIn={handleSignIn} />
      <HeroSection />
      
      {/* Section 2: This Is For You If */}
      <SectionDivider />
      <ThisIsForYouSection />
      
      {/* Section 3: What You Build (Interactive Product Demo) */}
      <SectionDivider />
      <WhatYouBuildSection />
      
      {/* Section 4: 7-Day Sprint */}
      <SectionDivider />
      <SevenDayBreakdown />
      
      {/* Section 5: Social Proof / Projects Ticker */}
      <SectionDivider />
      <TickerSection />
      
      {/* Section 6: Founder Story */}
      <SectionDivider />
      <FounderStorySection />
      
      {/* Section 7: Apply */}
      <SectionDivider />
      <ApplySection />
      
      {/* Section 8: Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage;
